import { useEffect, useRef } from "react";
import { useChristmasTheme } from "@/hooks/useChristmasTheme";

interface Flake {
  x: number;
  y: number;
  r: number;
  speed: number;
  drift: number;
  opacity: number;
  phase: number;
}

/**
 * Nieve ligera sobre toda la app (canvas, sin afectar la navegación).
 * - Respeta prefers-reduced-motion.
 * - Reduce considerablemente las partículas en móvil.
 * - Se puede desactivar desde el tema navideño.
 */
export default function ChristmasSnow() {
  const { enabled, snow, animations } = useChristmasTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const shouldRender = enabled && snow && animations && !reduceMotion;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (!shouldRender) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    let raf = 0;
    let flakes: Flake[] = [];
    let width = 0;
    let height = 0;

    const isMobile = window.innerWidth < 768;
    const count = isMobile ? 22 : 70;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const initFlakes = () => {
      flakes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 1 + Math.random() * 2.2,
        speed: 0.35 + Math.random() * 0.85,
        drift: -0.3 + Math.random() * 0.6,
        opacity: 0.22 + Math.random() * 0.45,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    resize();
    initFlakes();

    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      for (const f of flakes) {
        f.y += f.speed;
        f.x += f.drift + Math.sin(f.phase) * 0.2;
        f.phase += 0.01;
        if (f.y > height + 10) {
          f.y = -10;
          f.x = Math.random() * width;
        }
        if (f.x > width + 10) f.x = -10;
        if (f.x < -10) f.x = width + 10;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${f.opacity})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    const onResize = () => {
      resize();
      initFlakes();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [enabled, snow, animations]);

  if (!enabled || !snow) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[80]"
    />
  );
}